import React from "react";
import { Boxes, CircleArrowDown, CircleArrowUp, WalletCards, CircleDollarSign } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { formatIDR } from "../../lib/format";
import DashboardKpiCard from "./DashboardKpiCard";
import DashboardPanel from "./DashboardPanel";

const BRANCHES = ["jakarta", "surabaya", "bali"];
const LABELS = { jakarta: "Jakarta", surabaya: "Surabaya", bali: "Bali" };
const COLORS = {
  jakarta: "var(--neon-cyan-base)",
  surabaya: "var(--neon-mint-base)",
  bali: "var(--neon-yellow-base)",
  grid: "hsl(var(--border))",
  text: "hsl(var(--muted-foreground))",
};

function numeric(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function visibleBranches(branch) {
  return branch === "all" ? BRANCHES : [branch];
}

function hasValues(rows, branch) {
  return rows.some((row) => visibleBranches(branch).some((key) => numeric(row?.[key]) > 0));
}

function EmptyChartMessage({ children }) {
  return <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center text-center text-sm text-muted-foreground">{children}</div>;
}

export function OverviewTooltip({ active, payload, label, currency = false }) {
  if (!active || !Array.isArray(payload) || payload.length === 0) return null;
  const rows = payload.filter((entry) => entry.value !== undefined);
  const total = rows.reduce((sum, entry) => sum + numeric(entry.value), 0);
  return (
    <div className="min-w-44 rounded-md border border-border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-brutal">
      <p className="mb-1 font-semibold">{label}</p>
      <div className="space-y-1">
        {rows.map((entry) => (
          <div key={entry.dataKey} className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm" style={{ backgroundColor: entry.color || entry.stroke || entry.fill }} />{entry.name}</span>
            <span className="font-semibold tabular-nums">{currency ? formatIDR(entry.value) : numeric(entry.value).toLocaleString()}</span>
          </div>
        ))}
        <div className="flex justify-between gap-4 border-t border-border pt-1 font-semibold"><span>Total</span><span className="tabular-nums">{currency ? formatIDR(total) : total.toLocaleString()} {currency ? "" : "Units"}</span></div>
      </div>
    </div>
  );
}

function OverviewKpiGrid({ children, columns, testid }) {
  return <div className={`grid grid-cols-1 gap-3 ${columns}`} data-testid={testid}>{children}</div>;
}

export function CannibalDeviceOverview({ overview, branch }) {
  const data = Array.isArray(overview?.weekly_device_intake_by_branch) ? overview.weekly_device_intake_by_branch : [];
  const series = visibleBranches(branch);
  return (
    <DashboardPanel title="Cannibal Device Overview" description="Key metrics and recent intake activity for cannibal devices" testid="dashboard-cannibal-overview" className="h-full" contentClassName="space-y-5">
      <OverviewKpiGrid columns="sm:grid-cols-3" testid="dashboard-cannibal-kpis">
        <DashboardKpiCard label="Total Cannibal Devices" value={numeric(overview?.total_devices).toLocaleString()} icon={Boxes} accent="border-primary/30 bg-primary/10 text-primary" testid="kpi-cannibal-total" />
        <DashboardKpiCard label="Devices Added" helper="Selected Period" value={numeric(overview?.devices_added_selected_period).toLocaleString()} icon={CircleArrowDown} accent="border-info/30 bg-info/10 text-info" testid="kpi-cannibal-added" />
        <DashboardKpiCard label="Devices Transferred" helper="Selected Period" value={numeric(overview?.devices_transferred_selected_period).toLocaleString()} icon={CircleArrowUp} accent="border-movement/30 bg-movement/10 text-movement" testid="kpi-cannibal-transferred" />
      </OverviewKpiGrid>
      <div data-testid="dashboard-cannibal-weekly-chart">
        <div className="mb-3"><h3 className="text-sm font-semibold text-foreground">Weekly Cannibal Device Intake by Branch</h3><p className="text-xs text-muted-foreground">Last 5 weeks · stacked by branch</p></div>
        <div className="relative h-[260px] min-w-0" role="img" aria-label="Weekly Cannibal Device intake by Branch">
          {!hasValues(data, branch) ? <EmptyChartMessage>No Cannibal Device intake in these weeks.</EmptyChartMessage> : null}
          <ResponsiveContainer width="100%" height="100%"><BarChart data={data} margin={{ top: 12, right: 8, left: -14, bottom: 4 }}>
            <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: COLORS.text, fontSize: 11 }} tickLine={false} axisLine={{ stroke: COLORS.grid }} />
            <YAxis allowDecimals={false} tick={{ fill: COLORS.text, fontSize: 11 }} tickLine={false} axisLine={false} />
            <Tooltip content={<OverviewTooltip />} cursor={{ fill: "hsl(var(--muted) / 0.35)" }} />
            {series.map((key) => <Bar key={key} dataKey={key} name={LABELS[key]} stackId="intake" fill={COLORS[key]} radius={key === series[series.length - 1] ? [3, 3, 0, 0] : undefined} />)}
          </BarChart></ResponsiveContainer>
        </div>
      </div>
    </DashboardPanel>
  );
}

export function ServiceRevenueOverview({ overview, branch }) {
  const data = Array.isArray(overview?.monthly_revenue_by_branch) ? overview.monthly_revenue_by_branch : [];
  const series = visibleBranches(branch);
  return (
    <DashboardPanel title="Service Revenue Overview" description="All figures are based on Final Service Cost" testid="dashboard-service-revenue-overview" className="h-full" contentClassName="space-y-5">
      <OverviewKpiGrid columns="sm:grid-cols-2" testid="dashboard-service-revenue-kpis">
        <DashboardKpiCard label="Recognized Revenue" helper="Selected Period" value={formatIDR(overview?.revenue_selected_period)} secondary={`${numeric(overview?.revenue_generating_tickets).toLocaleString()} Revenue-Generating Tickets`} icon={CircleDollarSign} accent="border-success/30 bg-success/10 text-success" testid="kpi-revenue-recognized" />
        <DashboardKpiCard label="Potential Revenue" value={formatIDR(overview?.potential_revenue)} secondary={`${numeric(overview?.tickets_with_revenue_potential).toLocaleString()} Tickets with Revenue Potential`} icon={WalletCards} accent="border-info/30 bg-info/10 text-info" testid="kpi-potential-revenue" />
      </OverviewKpiGrid>
      <div data-testid="dashboard-service-revenue-chart">
        <div className="mb-3"><h3 className="text-sm font-semibold text-foreground">Monthly Revenue by Branch</h3><p className="text-xs text-muted-foreground">Last 5 months · Validation to Done tickets</p></div>
        <div className="relative h-[260px] min-w-0" role="img" aria-label="Monthly Service Revenue by Branch">
          {!hasValues(data, branch) ? <EmptyChartMessage>No Service Revenue in these months.</EmptyChartMessage> : null}
          <ResponsiveContainer width="100%" height="100%"><LineChart data={data} margin={{ top: 12, right: 16, left: -14, bottom: 4 }}>
            <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: COLORS.text, fontSize: 11 }} tickLine={false} axisLine={{ stroke: COLORS.grid }} />
            <YAxis tickFormatter={(value) => formatIDR(value)} tick={{ fill: COLORS.text, fontSize: 11 }} tickLine={false} axisLine={false} />
            <Tooltip content={<OverviewTooltip currency />} cursor={{ fill: "hsl(var(--muted) / 0.35)" }} />
            {series.map((key, index) => <Line key={key} type="monotone" dataKey={key} name={LABELS[key]} stroke={COLORS[key]} strokeWidth={2.5} strokeDasharray={index === 1 ? "6 3" : index === 2 ? "2 3" : undefined} dot={{ r: 3, fill: "hsl(var(--card))", strokeWidth: 2 }} activeDot={{ r: 5 }} />)}
          </LineChart></ResponsiveContainer>
        </div>
      </div>
    </DashboardPanel>
  );
}
